import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import {
  PlusIcon,
  TrashIcon,
  PaperClipIcon,
  XMarkIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import { Input, SearchableSelect, Button } from '@/components/common';
import { useInsurersLookup } from '@/hooks/useInsurers';
import {
  useInsuranceSubgroupsLookup,
  useProductsLookup,
  useProductVariantPolicyType,
  useVehicleCategoryGroup,
} from '@/hooks/useProducts';
import {
  emptyPremiumOption,
  isPremiumOptionBlank,
  localDateKey,
  premiumOptionErrors,
  type PremiumOptionDraft,
} from '../../premiumOptions';

/** `MotorQuoteFormData['vehicle_category']` -- declared here rather than
 *  imported from `schema.ts` (outside this task's file domain) since only the
 *  three literal values matter to this step. */
export type VehicleCategory = 'two_wheeler' | 'private_car' | 'commercial_vehicle';

interface PremiumOptionsStepProps {
  /** Selected InsuranceType UUID; scopes the Product dropdown. */
  insuranceTypeId?: string;
  insuranceTypeLabel?: string;
  /**
   * B14 -- the motor branch's selected vehicle type. Undefined on every other
   * branch (life, health, generic), which leaves the Product dropdown exactly
   * as it was: scoped to insurer + insurance type only, no vehicle-type
   * narrowing. Motor quotes get an additional `group` (and, for a chosen
   * commercial-vehicle subtype, `subgroup`) filter on top of that.
   */
  vehicleCategory?: VehicleCategory;
  /**
   * B5 -- the motor branch's Product Variant selection
   * (`MotorQuoteFormData['product_variant']`: `comprehensive` | `tp_only` |
   * `saod`). Undefined on every other branch, which leaves the Product
   * dropdown exactly as it was: no variant narrowing. Resolved to a real
   * `PolicyType` id by `useProductVariantPolicyType` (matching by name, not a
   * hardcoded id -- see that hook's doc for why); declared as a plain string
   * here rather than importing the wizard's own union type, for the same
   * reason `VehicleCategory` is redeclared above rather than imported.
   */
  productVariant?: string;
  options: PremiumOptionDraft[];
  onChange: (next: PremiumOptionDraft[]) => void;
  /** True once the user has tried to move on, so errors aren't shown pre-emptively. */
  showErrors?: boolean;
}

/**
 * Every row with its product cleared, or the SAME array when no row has one --
 * so a no-op reset does not hand React a new reference and re-render the page.
 */
export function clearPremiumOptionProducts(options: PremiumOptionDraft[]): PremiumOptionDraft[] {
  if (!options.some((o) => o.product || o.product_name)) return options;
  return options.map((o) => ({ ...o, product: '', product_name: '' }));
}

/**
 * B14 -- clears every row's product whenever the vehicle type the rows were
 * chosen under changes. MUST be called by the component that OWNS the options
 * state and stays mounted for the whole flow (`QuoteWizardPage`), never by the
 * step itself.
 *
 * Why the owner: on the motor branch this step is only mounted on "Product &
 * Premium Selection", and the vehicle type is chosen on the step before it. A
 * broker who picks a Two Wheeler product and goes back to Vehicle Details
 * UNMOUNTS these rows; switching to Private Car there and returning REMOUNTS
 * them already under the new type. A guard inside the row (a ref seeded with
 * the vehicle type on mount) therefore never sees a change, and the old Two
 * Wheeler product id stayed in state -- `SearchableSelect` could not find it
 * among the newly filtered options so the field looked empty, but the row
 * still validated and was POSTed onto the Private Car quote.
 *
 * The ref here is seeded with the type at the owner's mount, which is before
 * any option can have been chosen, so the first real selection of a type
 * (undefined -> 'two_wheeler') clears nothing that exists. The functional
 * update reads the latest rows, not a render-time snapshot.
 */
export function usePremiumOptionsVehicleCategoryReset(
  vehicleCategory: string | undefined,
  setOptions: Dispatch<SetStateAction<PremiumOptionDraft[]>>
): void {
  const chosenUnderRef = useRef(vehicleCategory);
  useEffect(() => {
    if (chosenUnderRef.current === vehicleCategory) return;
    chosenUnderRef.current = vehicleCategory;
    setOptions(clearPremiumOptionProducts);
  }, [vehicleCategory, setOptions]);
}

/**
 * Multi-insurer premium options, shown right after the insurance type on EVERY
 * quote flow (motor, life, health and the generic schema-driven one).
 *
 * The broker lists as many insurance companies as they have figures for --
 * Insurance Company, Product, Amount, Expiry Date, Attach File -- and then
 * carries on with the rest of the flow. Nothing is sent from here: there is no
 * quote request to attach an option to until submit succeeds, so the rows are
 * held as drafts and POSTed to `/quotes/requests/{id}/options/` afterwards.
 *
 * The whole step is optional. Leaving it empty gives exactly the behaviour the
 * flows had before -- which is what keeps the live GoDigit motor path
 * unchanged: it still submits the same payload and still gets its own priced
 * quotes back, and any rows typed here are simply added alongside them.
 */
export function PremiumOptionsStep({
  insuranceTypeId,
  insuranceTypeLabel,
  vehicleCategory,
  productVariant,
  options,
  onChange,
  showErrors,
}: PremiumOptionsStepProps) {
  const today = localDateKey(new Date());
  const { data: insurers, isLoading: insurersLoading } = useInsurersLookup();

  const insurerOptions = useMemo(
    () => (insurers ?? []).map((i) => ({ value: i.id, label: i.name })),
    [insurers]
  );

  function update(uid: string, patch: Partial<PremiumOptionDraft>) {
    onChange(options.map((o) => (o.uid === uid ? { ...o, ...patch } : o)));
  }

  function addRow() {
    onChange([...options, emptyPremiumOption()]);
  }

  function removeRow(uid: string) {
    const next = options.filter((o) => o.uid !== uid);
    onChange(next.length ? next : [emptyPremiumOption()]);
  }

  const filledCount = options.filter((o) => !isPremiumOptionBlank(o)).length;

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Premium Options</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Add the insurance companies you have premiums for
        {insuranceTypeLabel ? ` on this ${insuranceTypeLabel.toLowerCase()} quote` : ''}. You can add
        as many as you like, or skip this and add them later from the quote.
      </p>

      <div className="space-y-4">
        {options.map((option, index) => (
          <PremiumOptionRow
            key={option.uid}
            index={index}
            option={option}
            insuranceTypeId={insuranceTypeId}
            vehicleCategory={vehicleCategory}
            productVariant={productVariant}
            insurerOptions={insurerOptions}
            insurersLoading={insurersLoading}
            canRemove={options.length > 1 || !isPremiumOptionBlank(option)}
            errors={
              showErrors && !isPremiumOptionBlank(option)
                ? premiumOptionErrors(option, today)
                : {}
            }
            onChange={(patch) => update(option.uid, patch)}
            onRemove={() => removeRow(option.uid)}
          />
        ))}
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Button type="button" variant="outline" onClick={addRow} leftIcon={<PlusIcon className="w-4 h-4" />}>
          Add another insurance company
        </Button>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {filledCount === 0
            ? 'No premium options added yet'
            : `${filledCount} premium option${filledCount === 1 ? '' : 's'} ready`}
        </span>
      </div>
    </div>
  );
}

interface PremiumOptionRowProps {
  index: number;
  option: PremiumOptionDraft;
  /** Heading for the row. Defaults to "Option N" -- the quote detail screen
   *  uses one of these rows on its own, where a number would be a lie. */
  title?: string;
  /**
   * Prefix for the `id` given to every control in the row, so each `<label>`
   * is actually bound to its field. Without an id these components fall back to
   * `htmlFor={undefined}`, which binds the label to nothing -- the field is
   * then unreachable to a screen reader and to `getByLabelText`.
   */
  idPrefix?: string;
  insuranceTypeId?: string;
  /** B14. See `PremiumOptionsStepProps.vehicleCategory`. */
  vehicleCategory?: VehicleCategory;
  /** B5. See `PremiumOptionsStepProps.productVariant`. */
  productVariant?: string;
  insurerOptions: { value: string; label: string }[];
  insurersLoading: boolean;
  canRemove: boolean;
  errors: Record<string, string>;
  onChange: (patch: Partial<PremiumOptionDraft>) => void;
  onRemove: () => void;
}

/**
 * What the Product dropdown should say and whether it should accept input,
 * given every source of loading/error/emptiness that can apply to it.
 *
 * A pure function so B14's four states -- loading, a failed request, a
 * genuinely empty result and a normal list -- are each one assertion, not a
 * render pass with several queries mocked just right.
 */
export function resolveProductDropdownState(params: {
  hasInsurer: boolean;
  insurerName: string;
  vehicleCategory?: string;
  groupLabel?: string;
  groupLoading: boolean;
  groupIsError: boolean;
  /**
   * The groups lookup answered, with no error, but no `InsuranceGroup` name
   * matched this vehicle type (see `useVehicleCategoryGroup.notConfigured`) --
   * a catalog data problem (a renamed/deactivated group), distinct from a
   * failed request. Left unhandled, the row would fall through with
   * `group: undefined` and silently show the insurer's WHOLE catalog as if it
   * were correctly filtered -- exactly the bypass B14 exists to prevent.
   */
  groupNotConfigured?: boolean;
  /** B5. See `PremiumOptionsStepProps.productVariant`. Undefined/absent on a
   *  branch with no Product Variant step (life, health, generic) -- the same
   *  "no filter" default `vehicleCategory`/`groupLabel` have. */
  productVariant?: string;
  variantLabel?: string;
  variantLoading: boolean;
  variantIsError: boolean;
  /** The policy-types lookup answered, with no error, but no `PolicyType`
   *  matched this variant's name (see `useProductVariantPolicyType.
   *  notConfigured`) -- the B14 `groupNotConfigured` case, for the variant
   *  axis: block rather than silently show every variant's products. */
  variantNotConfigured?: boolean;
  productsLoading: boolean;
  productsIsError: boolean;
  productCount: number;
}): { disabled: boolean; placeholder: string; isError: boolean; helperText?: string } {
  if (!params.hasInsurer) {
    return { disabled: true, placeholder: 'Select an insurance company first', isError: false };
  }
  if (params.groupLoading || params.variantLoading || params.productsLoading) {
    return { disabled: true, placeholder: 'Loading…', isError: false };
  }
  // A failed vehicle-type lookup blocks the dropdown rather than silently
  // falling back to the unfiltered list -- an insurer's whole catalog is not
  // an honest substitute for "Two Wheeler products" when that is what was
  // asked for and the request to find out which ones simply failed.
  if (params.groupIsError) {
    return {
      disabled: true,
      placeholder: 'Unavailable',
      isError: true,
      helperText: 'Could not load vehicle types for the Product list — try again.',
    };
  }
  // The lookup answered fine, but no vehicle-type group matched by name (a
  // country admin renamed or deactivated "Two Wheeler" / "Private Car" /
  // "Commercial Vehicle"). Block, exactly as a failed request would -- an
  // unfiltered catalog is worse than an honest "we can't tell" here, because
  // it looks like a normal, correctly narrowed list.
  if (params.groupNotConfigured) {
    return {
      disabled: true,
      placeholder: 'Unavailable',
      isError: true,
      helperText: `No product catalog is set up for "${params.groupLabel ?? 'this vehicle type'}" — ask an administrator to check the product groups.`,
    };
  }
  // B5: a failed Product Variant (PolicyType) lookup, and the lookup
  // answering fine but no PolicyType matching this variant's name -- the same
  // two failure shapes as the vehicle-type group above, on the other axis.
  if (params.variantIsError) {
    return {
      disabled: true,
      placeholder: 'Unavailable',
      isError: true,
      helperText: 'Could not load product variants for the Product list — try again.',
    };
  }
  if (params.variantNotConfigured) {
    return {
      disabled: true,
      placeholder: 'Unavailable',
      isError: true,
      helperText: `No product catalog is set up for "${params.variantLabel ?? 'this product variant'}" — ask an administrator to check the policy types.`,
    };
  }
  if (params.productsIsError) {
    return {
      disabled: true,
      placeholder: 'Unavailable',
      isError: true,
      helperText: 'Products could not be loaded for this insurance company — try again.',
    };
  }
  if (params.productCount === 0) {
    // Both narrowings active: name the more specific one (the variant) --
    // "no Comprehensive products for X" is more actionable than repeating
    // the vehicle type on top of it.
    if (params.productVariant && params.variantLabel) {
      return {
        disabled: true,
        placeholder: `No ${params.variantLabel} products for ${params.insurerName || 'this insurer'}`,
        isError: false,
      };
    }
    if (params.vehicleCategory && params.groupLabel) {
      return {
        disabled: true,
        placeholder: `No ${params.groupLabel} products for ${params.insurerName || 'this insurer'}`,
        isError: false,
      };
    }
    return { disabled: true, placeholder: 'No products for this insurer', isError: false };
  }
  return { disabled: false, placeholder: 'Select product', isError: false };
}

export function PremiumOptionRow({
  index,
  option,
  title,
  idPrefix,
  insuranceTypeId,
  vehicleCategory,
  productVariant,
  insurerOptions,
  insurersLoading,
  canRemove,
  errors,
  onChange,
  onRemove,
}: PremiumOptionRowProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const ids = idPrefix ?? `premium-option-${index}`;

  // B14: the group this vehicle type maps to (e.g. "Two Wheeler"). Resolved
  // once per row rather than once per step so a mid-flow insurer/vehicle
  // change on any one row never disturbs the others.
  const vehicleGroup = useVehicleCategoryGroup(vehicleCategory);
  const isCommercialVehicle = vehicleCategory === 'commercial_vehicle';

  // B5: the PolicyType this Product Variant selection maps to (e.g.
  // "Standalone OD"), the same per-row resolution discipline as the group
  // above and for the same reason.
  const productVariantType = useProductVariantPolicyType(insuranceTypeId, productVariant);

  // A chosen commercial-vehicle subtype (GCV/PCV/Miscellaneous) narrows the
  // Product dropdown further, by `subgroup`. There is no such field upstream
  // on the wizard's `MotorQuoteFormData` yet (only the coarse vehicle
  // category is captured there), so this is asked for right here, as an
  // optional filter local to the row -- it exists only to narrow the Product
  // list and is never itself submitted.
  const { data: cvSubgroups, isLoading: cvSubgroupsLoading } = useInsuranceSubgroupsLookup(
    isCommercialVehicle ? vehicleGroup.groupId : undefined
  );
  const [cvSegment, setCvSegment] = useState('');

  // The CV segment is this row's own local filter, so a vehicle-type change
  // while the row is mounted resets it here. The PRODUCT is deliberately NOT
  // cleared here: the row is unmounted while the vehicle type is being changed
  // on the wizard, so a row-level guard never fires there. Clearing the product
  // belongs to the owner of the options state -- see
  // `usePremiumOptionsVehicleCategoryReset`, called by `QuoteWizardPage`. (The
  // quote detail screen's vehicle type is fixed for the quote, so it has no
  // such change to handle.) The API refuses a mismatched product regardless.
  const prevVehicleCategoryRef = useRef(vehicleCategory);
  useEffect(() => {
    if (prevVehicleCategoryRef.current !== vehicleCategory) {
      prevVehicleCategoryRef.current = vehicleCategory;
      setCvSegment('');
    }
  }, [vehicleCategory]);

  const cvSegmentOptions = useMemo(
    () => (cvSubgroups ?? []).map((s) => ({ value: s.id, label: s.name })),
    [cvSubgroups]
  );

  // Products are scoped to the insurer and the insurance type as before, plus
  // -- B14 -- the vehicle-type group (and, for a chosen CV subtype, the
  // subgroup) -- plus, B5, the Product Variant's PolicyType. Held back
  // (`enabled: false`) until BOTH resolve so the dropdown does not flash the
  // insurer's WHOLE catalog for an instant before narrowing -- a resolved
  // `vehicleCategory`/`productVariant` with nothing to filter by YET is "not
  // ready", not "no filter" (the same reasoning as `groupResolving` already
  // established, extended to the second axis).
  const groupResolving = !!vehicleCategory && vehicleGroup.isLoading;
  const variantResolving = !!productVariant && productVariantType.isLoading;
  const { data: products, isLoading: productsLoading, isError: productsIsError } = useProductsLookup(
    option.insurer
      ? {
          insurer: option.insurer,
          insurance_type: insuranceTypeId,
          group: vehicleGroup.groupId,
          subgroup: isCommercialVehicle ? cvSegment || undefined : undefined,
          policy_type: productVariantType.policyTypeId,
        }
      : undefined,
    { enabled: !!option.insurer && !groupResolving && !variantResolving }
  );

  const productOptions = useMemo(
    () => (products ?? []).map((p) => ({ value: p.id, label: p.name })),
    [products]
  );

  const productDropdown = resolveProductDropdownState({
    hasInsurer: !!option.insurer,
    insurerName: option.insurer_name,
    vehicleCategory,
    groupLabel: vehicleGroup.groupLabel,
    groupLoading: groupResolving,
    groupIsError: vehicleGroup.isError,
    groupNotConfigured: vehicleGroup.notConfigured,
    productVariant,
    variantLabel: productVariantType.policyTypeLabel,
    variantLoading: variantResolving,
    variantIsError: productVariantType.isError,
    variantNotConfigured: productVariantType.notConfigured,
    productsLoading,
    productsIsError,
    productCount: productOptions.length,
  });

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
          <BuildingOffice2Icon className="w-4 h-4 text-gray-400" />
          {title ?? `Option ${index + 1}`}
        </span>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remove option ${index + 1}`}
            className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SearchableSelect
          id={`${ids}-insurer`}
          label="Insurance Company"
          options={insurerOptions}
          value={option.insurer}
          onChange={(value) => {
            const picked = insurerOptions.find((i) => i.value === value);
            // Changing the insurer invalidates the product: the product list is
            // scoped by insurer, so keeping the old id would submit a pairing
            // the insurer does not sell. The commercial-vehicle segment filter
            // is local to (insurer, vehicle type), not to the product itself,
            // but resetting it here too keeps this row's product exactly as
            // narrow as what is actually shown for the newly picked insurer.
            setCvSegment('');
            onChange({
              insurer: value,
              insurer_name: picked?.label ?? '',
              product: '',
              product_name: '',
            });
          }}
          placeholder={insurersLoading ? 'Loading…' : 'Select insurance company'}
          error={errors.insurer}
          required
        />
        {/* B14. Only offered once there is an actual Commercial Vehicle group
            to narrow, and only once its subgroups (GCV/PCV/...) have loaded --
            an insurer with none configured shows no empty selector. Optional:
            leaving it blank keeps every commercial-vehicle subtype in the
            Product list below, exactly as it was before this narrowing existed. */}
        {isCommercialVehicle && !!vehicleGroup.groupId && !cvSubgroupsLoading && cvSegmentOptions.length > 0 && (
          <SearchableSelect
            id={`${ids}-cv-segment`}
            label="Vehicle Segment (optional)"
            options={cvSegmentOptions}
            value={cvSegment}
            onChange={(value) => {
              setCvSegment(value);
              // The eligible product set just changed; keep a stale pick from
              // resubmitting a product outside the newly narrowed segment.
              onChange({ product: '', product_name: '' });
            }}
            placeholder="All Commercial Vehicle"
          />
        )}
        <SearchableSelect
          id={`${ids}-product`}
          label="Product"
          options={productOptions}
          value={option.product}
          onChange={(value) => {
            const picked = productOptions.find((p) => p.value === value);
            onChange({ product: value, product_name: picked?.label ?? '' });
          }}
          placeholder={productDropdown.placeholder}
          disabled={productDropdown.disabled}
          error={errors.product}
          required
        />
        {productDropdown.isError && (
          <p className="sm:col-span-2 -mt-2 text-xs text-red-600 dark:text-red-400">{productDropdown.helperText}</p>
        )}
        <Input
          id={`${ids}-amount`}
          label="Amount"
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder="Total premium payable"
          value={option.total_premium}
          onChange={(e) => onChange({ total_premium: e.target.value })}
          error={errors.total_premium}
          required
        />
        <Input
          id={`${ids}-expiry`}
          label="Expiry Date"
          type="date"
          value={option.valid_until}
          onChange={(e) => onChange({ valid_until: e.target.value })}
          error={errors.valid_until}
          required
        />
      </div>

      <div className="mt-4">
        <label
          htmlFor={`${ids}-file`}
          className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1"
        >
          Attach File
        </label>
        {option.file ? (
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
            <PaperClipIcon className="w-4 h-4 text-gray-400" />
            <span className="truncate max-w-xs">{option.file.name}</span>
            <button
              type="button"
              onClick={() => {
                onChange({ file: null });
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              aria-label="Remove attachment"
              className="text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Optional. The customer can see this file on the shared quote link.
          </p>
        )}
        <input
          id={`${ids}-file`}
          ref={fileInputRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg,.webp"
          onChange={(e) => onChange({ file: e.target.files?.[0] ?? null })}
          className="block w-full text-sm text-gray-600 dark:text-gray-300 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:bg-white dark:file:bg-gray-800 file:text-gray-700 dark:file:text-gray-200 hover:file:bg-gray-50 dark:hover:file:bg-gray-700/50 mt-1"
        />
      </div>
    </div>
  );
}

export default PremiumOptionsStep;
